import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Crown, ShieldCheck, Gift, Ticket, Mail, Calendar } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, StatusBadge } from "@/components/admin/AdminShared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function AdminUsers() {
  useSEO("Admin · Users");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [vipOpen, setVipOpen] = useState(false);
  const [vipForm, setVipForm] = useState({ status: "active", source: "subscription", duration_days: "", lifetime: false });

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => !q || u.email.toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()));

  const openDetail = (u) => api.get(`/admin/users/${u.id}`).then((r) => setDetail(r.data)).catch(() => toast.error("Failed to load"));
  const setRole = async (id, role) => { try { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); if (detail) openDetail({ id }); } catch { toast.error("Failed"); } };

  const saveVip = async () => {
    try {
      await api.post(`/admin/vip/${detail.user.id}/activate`, {
        status: vipForm.status, source: vipForm.source,
        duration_days: vipForm.duration_days ? Number(vipForm.duration_days) : null,
        lifetime: vipForm.lifetime,
      });
      toast.success("VIP updated"); setVipOpen(false); load(); openDetail(detail.user);
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <AdminHeader title="Users" subtitle={`${users.length} members`} />
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" data-testid="user-search" className="pl-10 bg-white/5 border-white/10 h-9" />
      </div>
      <div className="card-surface rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-white/10">
            <th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th>
            <th className="px-4 py-3 font-medium">VIP</th><th className="px-4 py-3 font-medium">Broker</th>
            <th className="px-4 py-3 font-medium">Joined</th><th className="px-4 py-3 font-medium"></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No members found.</td></tr>
              : filtered.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => openDetail(u)} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 text-white">{u.name}{u.role === "admin" && <span className="ml-2 text-[10px] text-brand-cyan">ADMIN</span>}</td>
                  <td className="px-4 py-3 text-slate-400">{u.email}</td>
                  <td className="px-4 py-3"><StatusBadge status={u.vip?.active ? "active" : "inactive"} /></td>
                  <td className="px-4 py-3"><StatusBadge status={u.broker_status === "none" ? "inactive" : u.broker_status} /></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmt(u.created_at)}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" className="h-8 border-white/15 text-white" data-testid={`view-user-${u.id}`}>View</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Member detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-brand-surface border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full gradient-brand flex items-center justify-center text-brand-navy font-bold">{(detail.user.name || "U").charAt(0)}</div>
                  {detail.user.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-white/5 p-3"><span className="text-slate-500 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</span><p className="text-white mt-0.5 truncate">{detail.user.email}</p></div>
                  <div className="rounded-lg bg-white/5 p-3"><span className="text-slate-500 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Joined</span><p className="text-white mt-0.5">{fmt(detail.user.created_at)}</p></div>
                  <div className="rounded-lg bg-white/5 p-3"><span className="text-slate-500">Referral code</span><p className="text-white mt-0.5 font-mono">{detail.user.referral_code}</p></div>
                  <div className="rounded-lg bg-white/5 p-3"><span className="text-slate-500 flex items-center gap-1.5"><Gift className="h-3.5 w-3.5" /> Referrals</span><p className="text-white mt-0.5">{detail.referral_count}</p></div>
                </div>

                <div className="rounded-lg border border-white/10 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white flex items-center gap-1.5"><Crown className="h-4 w-4 text-brand-cyan" /> VIP</span>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={detail.user.vip?.active ? "active" : "inactive"} />
                      <Button size="sm" className="h-7 gradient-brand text-brand-navy font-semibold text-xs" onClick={() => { setVipForm({ status: "active", source: detail.user.vip?.source || "subscription", duration_days: "", lifetime: detail.user.vip?.lifetime || false }); setVipOpen(true); }} data-testid="detail-manage-vip">Manage</Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">{detail.user.vip?.active ? `Source: ${detail.user.vip?.source || "—"}${detail.user.vip?.lifetime ? " · Lifetime" : detail.user.vip?.expires_at ? ` · Expires ${fmt(detail.user.vip.expires_at)}` : ""}` : "No active VIP membership."}</p>
                </div>

                <div className="rounded-lg border border-white/10 p-3">
                  <span className="font-semibold text-white flex items-center gap-1.5 mb-2"><ShieldCheck className="h-4 w-4 text-brand-cyan" /> Broker Verifications</span>
                  {detail.verifications.length === 0 ? <p className="text-xs text-slate-500">None submitted.</p>
                    : detail.verifications.map((v) => (
                      <div key={v.id} className="flex items-center justify-between py-1.5 border-t border-white/5 first:border-0">
                        <span className="text-slate-300">{v.broker_name} <span className="text-slate-600 font-mono">· {v.account_uid}</span></span>
                        <StatusBadge status={v.status} />
                      </div>
                    ))}
                </div>

                <div className="rounded-lg border border-white/10 p-3">
                  <span className="font-semibold text-white flex items-center gap-1.5 mb-2"><Ticket className="h-4 w-4 text-brand-cyan" /> Support Tickets</span>
                  {detail.tickets.length === 0 ? <p className="text-xs text-slate-500">No tickets.</p>
                    : detail.tickets.map((t) => (
                      <div key={t.id} className="flex items-center justify-between py-1.5 border-t border-white/5 first:border-0">
                        <span className="text-slate-300 truncate">{t.subject}</span>
                        <StatusBadge status={t.status} />
                      </div>
                    ))}
                </div>

                <div className="flex items-center justify-between rounded-lg bg-white/5 p-3">
                  <span className="text-slate-400">Role</span>
                  <Select value={detail.user.role} onValueChange={(v) => setRole(detail.user.id, v)}>
                    <SelectTrigger className="w-28 h-8 bg-white/5 border-white/10 text-xs" data-testid="detail-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage VIP */}
      <Dialog open={vipOpen} onOpenChange={setVipOpen}>
        <DialogContent className="bg-brand-surface border-white/10">
          <DialogHeader><DialogTitle className="text-white">Manage VIP · {detail?.user?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-300 mb-1.5">Status</p>
              <Select value={vipForm.status} onValueChange={(v) => setVipForm({ ...vipForm, status: v })}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="vip-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-sm text-slate-300 mb-1.5">Source</p>
              <Select value={vipForm.source} onValueChange={(v) => setVipForm({ ...vipForm, source: v })}>
                <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="subscription">Subscription</SelectItem><SelectItem value="broker">Broker Benefit</SelectItem><SelectItem value="promo">Promo Code</SelectItem><SelectItem value="admin">Admin Grant</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-sm text-slate-300 mb-1.5">Duration (days) — empty for no expiry</p>
              <Input type="number" value={vipForm.duration_days} onChange={(e) => setVipForm({ ...vipForm, duration_days: e.target.value })} data-testid="vip-duration" className="bg-white/5 border-white/10" disabled={vipForm.lifetime} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={vipForm.lifetime} onChange={(e) => setVipForm({ ...vipForm, lifetime: e.target.checked })} data-testid="vip-lifetime" /> Lifetime access</label>
          </div>
          <DialogFooter><Button onClick={saveVip} className="gradient-brand text-brand-navy font-semibold" data-testid="vip-save">Save VIP</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
